import React, { useEffect, useState, useRef } from 'react';
import DateInput from '../../components/DateInput';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/axios';
import { toast } from 'react-toastify';

// Friendly labels for auto-generated remark text.
const ORDER_FIELD_LABELS = {
  date_of_order: 'Date of Order',
  indent_no: 'Indent No',
  indent_date: 'Indent Date',
  shipper_id: 'Shipper',
  shipper_name: 'Shipper',
  contact_person: 'Contact Person',
  quality: 'Material Quality',
  origin_country: 'Origin Country',
  shipping_line: 'Shipping Line (Final)',
  shipping_lines: 'Shipping Lines',
  icd_detail: 'ICD Location',
  icd_details: 'ICD Locations',
  port_of_discharge: 'Port of Discharge',
  final_destination: 'Final Destination',
  contract_weight_mt: 'Contract Weight (MT)',
  rate_usd: 'Rate (USD/MT)',
  rate_variation: 'Rate Variation',
  weight_variation: 'Weight Variation (MT)',
  discount_amount: 'Discount (USD)',
  final_rate: 'Final Rate (USD/MT)',
  no_of_containers: 'No. of Containers',
  avg_loading: 'Avg Loading (MT/cont)',
  payment_term: 'Payment Term',
  sblc_applied_date: 'SBLC Applied Date',
  sblc_approve_date: 'SBLC Approve Date',
  remarks: 'Remarks / Notes',
};

export default function OrderForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState({ order_no:'', date_of_order:'', indent_no:'', indent_date:'', shipper_id:'', shipper_name:'', contact_person:'', quality:'', origin_country:'', shipping_line:'', shipping_lines:[], icd_details:[], contract_weight_mt:'', rate_usd:'', rate_variation:'0', weight_variation:'', discount_amount:'', final_rate:'', no_of_containers:'', avg_loading:'', payment_term:'', sblc_applied_date:'', sblc_approve_date:'', remarks:'', port_of_discharge:'', final_destination:'' });
  const [original, setOriginal] = useState(null); // snapshot for change-tracking
  const [shippers, setShippers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [shippingLines, setShippingLines] = useState([]);
  const [icdLocations, setIcdLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [remarkModal, setRemarkModal] = useState(null); // { changes, note }
  const [shipOpen, setShipOpen] = useState(false);
  const shipRef = useRef(null);
  const [icdOpen, setIcdOpen] = useState(false);
  const icdRef = useRef(null);
  useEffect(() => {
    const onDocClick = e => {
      if (shipRef.current && !shipRef.current.contains(e.target)) setShipOpen(false);
      if (icdRef.current && !icdRef.current.contains(e.target)) setIcdOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/masters/shippers'), api.get('/masters/quality-grades'),
      api.get('/masters/origin-countries'), api.get('/masters/shipping-lines'),
      api.get('/masters/icd-locations')
    ]).then(([s,q,o,sl,icd]) => {
      setShippers(s.data); setQualities(q.data); setOrigins(o.data);
      setShippingLines(sl.data); setIcdLocations(icd.data);
    });
    if (isEdit) {
      api.get(`/orders/${id}`).then(r => {
        const o = r.data;
        const loaded = { order_no: o.order_no||'', date_of_order: o.date_of_order?.split('T')[0]||'', indent_no: o.indent_no||'', indent_date: o.indent_date?.split('T')[0]||'', shipper_id: o.shipper_id||'', shipper_name: o.shipper_name||'', contact_person: o.contact_person||'', quality: o.quality||'', origin_country: o.origin_country||'', shipping_line: o.shipping_line||'', shipping_lines: Array.isArray(o.shipping_lines) ? o.shipping_lines : [], icd_details: Array.isArray(o.icd_details) && o.icd_details.length ? o.icd_details : (o.icd_detail ? [o.icd_detail] : []), contract_weight_mt: o.contract_weight_mt||'', rate_usd: o.rate_usd||'', rate_variation: o.rate_variation||'0', weight_variation: o.weight_variation||'', discount_amount: o.discount_amount||'', final_rate: o.final_rate||'', no_of_containers: o.no_of_containers||'', avg_loading: o.avg_loading||'', payment_term: o.payment_term||'', sblc_applied_date: o.sblc_applied_date?.split('T')[0]||'', sblc_approve_date: o.sblc_approve_date?.split('T')[0]||'', remarks: o.remarks||'', port_of_discharge: o.port_of_discharge||'', final_destination: o.final_destination||'' };
        setForm(loaded);
        setOriginal({ ...loaded, _id: o.id, _step: o.workflow_step || 0 });
      });
    } else {
      const now = new Date();
      api.get('/orders/next-no').then(r => {
        setForm(f => ({...f, order_no: r.data.order_no || '', date_of_order: now.toISOString().split('T')[0]}));
      }).catch(() => {
        setForm(f => ({...f, date_of_order: now.toISOString().split('T')[0]}));
      });
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  // Final Rate auto = rate_usd − discount_amount.
  // Average Loading auto = contract_weight_mt ÷ no_of_containers.
  const recalcFinal = (next) => {
    const rate = parseFloat(next.rate_usd) || 0;
    const disc = parseFloat(next.discount_amount) || 0;
    if (rate > 0) {
      next.final_rate = (rate - disc).toFixed(4);
    }
    const wt = parseFloat(next.contract_weight_mt) || 0;
    const containers = parseFloat(next.no_of_containers) || 0;
    if (wt > 0 && containers > 0) {
      next.avg_loading = (wt / containers).toFixed(3);
    } else {
      next.avg_loading = '';
    }
    return next;
  };
  const setWithRecalc = (k, v) => setForm(f => recalcFinal({ ...f, [k]: v }));

  const handleShipperChange = e => {
    const s = shippers.find(x => x.id === parseInt(e.target.value));
    setForm(f => ({...f, shipper_id: e.target.value, shipper_name: s?.name||'', contact_person: s?.contact_person||''}));
  };

  // Compare form vs original; ignore internal/auto fields. Returns array of {field, old_value, new_value}.
  const computeChanges = () => {
    if (!original) return [];
    // icd_details is intentionally excluded — updating ICD on an order
    // should not trigger / require a remark, nor get a remark flag.
    const SKIP = new Set(['order_no', 'icd_details']);
    const out = [];
    for (const k of Object.keys(form)) {
      if (SKIP.has(k)) continue;
      const a = original[k]; const b = form[k];
      const aS = Array.isArray(a) ? JSON.stringify(a) : (a == null ? '' : String(a));
      const bS = Array.isArray(b) ? JSON.stringify(b) : (b == null ? '' : String(b));
      if (aS !== bS) out.push({ scope: 'order', record_id: original._id, field: k, old_value: a == null ? '' : (Array.isArray(a) ? a.join(', ') : String(a)), new_value: b == null ? '' : (Array.isArray(b) ? b.join(', ') : String(b)) });
    }
    return out;
  };

  const doSave = async (remarkNote) => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/orders/${id}`, form);
        if (remarkNote) {
          await api.post(`/remarks/order/${id}`, { note: remarkNote, fields: computeChanges() });
        }
        toast.success('Order updated');
        navigate(`/orders/${id}`);
      } else {
        const r = await api.post('/orders', form);
        toast.success('Order created');
        navigate(`/orders/${r.data.id}`);
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); setRemarkModal(null); }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.order_no || !form.date_of_order) { toast.error('Order No and Date are required'); return; }
    if (form.payment_term === 'SBLC' && (!form.sblc_applied_date || !form.sblc_approve_date)) {
      toast.error('SBLC Applied Date and SBLC Approve Date are required for SBLC');
      return;
    }
    // Post-Indent edits (workflow_step >= 2) → auto-log a remark when fields change.
    if (isEdit && original && (original._step || 0) >= 2) {
      const changes = computeChanges();
      if (changes.length > 0) {
        const note = changes.map(c => `${ORDER_FIELD_LABELS[c.field] || c.field}: ${c.old_value || '—'} → ${c.new_value || '—'}`).join('\n');
        return doSave(note);
      }
    }
    doSave(null);
  };

  return (
    <div>
      <div className="page-header">
        <div><h2>{isEdit ? 'Edit Order' : 'New Import Order'}</h2><p>Step 1–2: Order & Indent Management</p></div>
        <button className="btn btn-secondary" onClick={() => navigate(isEdit ? `/orders/${id}` : '/orders')}>Cancel</button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card mb-2">
          <div className="section-title">Order Information</div>
          <div className="form-row-4">
            <div className="form-group"><label>Order No <span className="req">*</span></label><input className="form-control" value={form.order_no} readOnly /></div>
            <div className="form-group"><label>Date of Order <span className="req">*</span></label><DateInput className="form-control" value={form.date_of_order} onChange={e=>set('date_of_order',e.target.value)} required /></div>
            <div className="form-group"><label>Indent No</label><input className="form-control" value={form.indent_no} onChange={e=>set('indent_no',e.target.value)} /></div>
            <div className="form-group"><label>Indent Date</label><DateInput className="form-control" value={form.indent_date} onChange={e=>set('indent_date',e.target.value)} /></div>
          </div>
          <div className="form-row-4">
            <div className="form-group">
              <label>Shipper / Supplier</label>
              <select className="form-control" value={form.shipper_id} onChange={handleShipperChange}>
                <option value="">— Select Shipper —</option>
                {shippers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Contact Person</label><input className="form-control" value={form.contact_person} onChange={e=>set('contact_person',e.target.value)} /></div>
            <div className="form-group" />
            <div className="form-group" />
          </div>
        </div>

        <div className="card mb-2">
          <div className="section-title">Material & Trade Details</div>
          <div className="form-row-4">
            <div className="form-group">
              <label>Material Quality</label>
              <select className="form-control" value={form.quality} onChange={e=>set('quality',e.target.value)}>
                <option value="">— Select Quality —</option>
                {qualities.map(q=><option key={q.id}>{q.grade}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Origin Country</label>
              <select className="form-control" value={form.origin_country} onChange={e=>set('origin_country',e.target.value)}>
                <option value="">— Select Country —</option>
                {origins.map(o=><option key={o.id}>{o.country_name}</option>)}
              </select>
            </div>
            <div className="form-group" ref={shipRef} style={{position:'relative'}}>
              <label>Shipping Lines</label>
              <div onClick={()=>setShipOpen(o=>!o)} style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'4px',padding:'6px 28px 6px 8px',border:'1px solid var(--border,#cbd5e1)',borderRadius:'6px',minHeight:'38px',background:'#fff',cursor:'pointer',position:'relative'}}>
                {(form.shipping_lines||[]).length === 0 && <span style={{color:'#94a3b8',fontSize:'13px'}}>— Select shipping lines —</span>}
                {(form.shipping_lines||[]).map(name => (
                  <span key={name} style={{display:'inline-flex',alignItems:'center',gap:'4px',background:'#e0f2fe',color:'#0369a1',padding:'2px 6px',borderRadius:'10px',fontSize:'11px',fontWeight:500}}>
                    {name}
                    <button type="button" onClick={e=>{
                      e.stopPropagation();
                      const next = (form.shipping_lines||[]).filter(n=>n!==name);
                      const finalGone = form.shipping_line === name;
                      setForm(f=>({...f, shipping_lines: next, ...(finalGone ? { shipping_line: '' } : {})}));
                    }} style={{background:'transparent',border:0,color:'inherit',fontSize:'13px',cursor:'pointer',padding:0,lineHeight:1}}>×</button>
                  </span>
                ))}
                <span style={{position:'absolute',right:'10px',top:'50%',transform:'translateY(-50%)',fontSize:'10px',color:'#64748b'}}>▼</span>
              </div>
              {shipOpen && (
                <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:'#fff',border:'1px solid #cbd5e1',borderRadius:'6px',boxShadow:'0 8px 20px rgba(15,23,42,0.12)',zIndex:50,maxHeight:'240px',overflowY:'auto'}}>
                  {shippingLines.length === 0 ? (
                    <div style={{padding:'10px',fontSize:'12px',color:'#94a3b8',textAlign:'center'}}>No shipping lines available — add via Master Data.</div>
                  ) : shippingLines.map(sl => {
                    const checked = (form.shipping_lines||[]).includes(sl.name);
                    const isFinal = form.shipping_line === sl.name;
                    return (
                      <div key={sl.id} onClick={()=>{
                        const cur = form.shipping_lines || [];
                        if (checked) {
                          const next = cur.filter(n=>n!==sl.name);
                          setForm(f=>({...f, shipping_lines: next, ...(f.shipping_line===sl.name ? { shipping_line: '' } : {})}));
                        } else {
                          setForm(f=>({...f, shipping_lines: [...cur, sl.name]}));
                        }
                      }} style={{display:'flex',alignItems:'center',gap:'8px',padding:'8px 10px',cursor:'pointer',background: checked ? '#f1f5f9' : '#fff',fontSize:'13px'}} onMouseEnter={e=>e.currentTarget.style.background='#e0f2fe'} onMouseLeave={e=>e.currentTarget.style.background = checked ? '#f1f5f9' : '#fff'}>
                        <input type="checkbox" checked={checked} readOnly style={{margin:0}} />
                        <span style={{flex:1}}>{sl.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="form-group" ref={icdRef} style={{position:'relative'}}>
              <label>ICD Locations</label>
              <div onClick={()=>setIcdOpen(o=>!o)} style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'4px',padding:'6px 28px 6px 8px',border:'1px solid var(--border,#cbd5e1)',borderRadius:'6px',minHeight:'38px',background:'#fff',cursor:'pointer',position:'relative'}}>
                {(form.icd_details||[]).length === 0 && <span style={{color:'#94a3b8',fontSize:'13px'}}>— Select ICD —</span>}
                {(form.icd_details||[]).map(name => (
                  <span key={name} style={{display:'inline-flex',alignItems:'center',gap:'4px',background:'#e0f2fe',color:'#0369a1',padding:'2px 6px',borderRadius:'10px',fontSize:'11px',fontWeight:500}}>
                    {name}
                    <button type="button" onClick={e=>{
                      e.stopPropagation();
                      setForm(f=>({...f, icd_details: (f.icd_details||[]).filter(n=>n!==name)}));
                    }} style={{background:'transparent',border:0,color:'inherit',fontSize:'13px',cursor:'pointer',padding:0,lineHeight:1}}>×</button>
                  </span>
                ))}
                <span style={{position:'absolute',right:'10px',top:'50%',transform:'translateY(-50%)',fontSize:'10px',color:'#64748b'}}>▼</span>
              </div>
              {icdOpen && (
                <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:'#fff',border:'1px solid #cbd5e1',borderRadius:'6px',boxShadow:'0 8px 20px rgba(15,23,42,0.12)',zIndex:50,maxHeight:'240px',overflowY:'auto'}}>
                  {icdLocations.length === 0 ? (
                    <div style={{padding:'10px',fontSize:'12px',color:'#94a3b8',textAlign:'center'}}>No ICD locations available — add via Master Data.</div>
                  ) : icdLocations.map(loc => {
                    const checked = (form.icd_details||[]).includes(loc.location_name);
                    return (
                      <div key={loc.id} onClick={()=>{
                        const cur = form.icd_details || [];
                        setForm(f=>({...f, icd_details: checked ? cur.filter(n=>n!==loc.location_name) : [...cur, loc.location_name]}));
                      }} style={{display:'flex',alignItems:'center',gap:'8px',padding:'8px 10px',cursor:'pointer',background: checked ? '#f1f5f9' : '#fff',fontSize:'13px'}} onMouseEnter={e=>e.currentTarget.style.background='#e0f2fe'} onMouseLeave={e=>e.currentTarget.style.background = checked ? '#f1f5f9' : '#fff'}>
                        <input type="checkbox" checked={checked} readOnly style={{margin:0}} />
                        <span style={{flex:1}}>{loc.location_name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="form-row-4">
            <div className="form-group"><label>Port of Discharge</label><input className="form-control" value={form.port_of_discharge} onChange={e=>set('port_of_discharge',e.target.value)} /></div>
            <div className="form-group"><label>Final Destination</label><input className="form-control" value={form.final_destination} onChange={e=>set('final_destination',e.target.value)} /></div>
            <div className="form-group"><label>Contract Weight (MT)</label><input className="form-control" type="number" step="0.001" value={form.contract_weight_mt} onChange={e=>setWithRecalc('contract_weight_mt',e.target.value)} /></div>
            <div className="form-group"><label>Weight Variation (+/− MT)</label><input className="form-control" type="number" step="0.001" value={form.weight_variation} onChange={e=>set('weight_variation',e.target.value)} /></div>
          </div>
          <div className="form-row-4">
            <div className="form-group"><label>Rate (USD/MT)</label><input className="form-control" type="number" step="0.0001" value={form.rate_usd} onChange={e=>setWithRecalc('rate_usd',e.target.value)} /></div>
            <div className="form-group"><label>Discount (USD)</label><input className="form-control" type="number" step="0.01" value={form.discount_amount} onChange={e=>setWithRecalc('discount_amount',e.target.value)} /></div>
            <div className="form-group"><label>Final Rate (USD/MT)</label><input className="form-control" type="number" step="0.0001" value={form.final_rate} onChange={e=>set('final_rate',e.target.value)} /></div>
            <div className="form-group"><label>No. of Containers</label><input className="form-control" type="number" step="1" value={form.no_of_containers} onChange={e=>setWithRecalc('no_of_containers',e.target.value)} /></div>
          </div>
          <div className="form-row-4">
            <div className="form-group"><label>Average Loading (MT/container)</label><input className="form-control" type="number" step="0.001" value={form.avg_loading} onChange={e=>set('avg_loading',e.target.value)} /></div>
            <div className="form-group"><label>Payment Term</label><select className="form-control" value={form.payment_term} onChange={e=>{
              const v = e.target.value;
              setForm(f => ({...f, payment_term: v, ...(v !== 'SBLC' ? { sblc_applied_date: '', sblc_approve_date: '' } : {})}));
            }}>
              <option value="">— Select —</option>
              <option value="DP">DP</option>
              <option value="SBLC">SBLC</option>
            </select></div>
            {form.payment_term === 'SBLC' && (
              <>
                <div className="form-group"><label>SBLC Applied Date <span className="req">*</span></label><DateInput className="form-control" value={form.sblc_applied_date} onChange={e=>set('sblc_applied_date',e.target.value)} required /></div>
                <div className="form-group"><label>SBLC Approve Date <span className="req">*</span></label><DateInput className="form-control" value={form.sblc_approve_date} onChange={e=>set('sblc_approve_date',e.target.value)} required /></div>
              </>
            )}
          </div>
          <div className="form-group"><label>Remarks / Notes</label><textarea className="form-control" rows={3} value={form.remarks} onChange={e=>set('remarks',e.target.value)} /></div>
        </div>

        <div className="btn-group">
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Update Order' : 'Create Order')}</button>
          <button className="btn btn-secondary" type="button" onClick={() => navigate(isEdit ? `/orders/${id}` : '/orders')}>Cancel</button>
        </div>
      </form>

      {remarkModal && (
        <div onClick={()=>!saving && setRemarkModal(null)} style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'12px',padding:'24px',width:'min(560px,94vw)',maxHeight:'85vh',overflow:'auto',boxShadow:'0 18px 48px rgba(15,23,42,0.25)'}}>
            <h3 style={{margin:'0 0 6px',fontSize:'17px',fontWeight:700}}>Remark for these changes</h3>
            <p style={{color:'var(--text-muted)',fontSize:'12px',marginTop:0}}>Order is past the Indent stage. Briefly note <i>why</i> these fields are changing — it'll be flagged in the print PDF.</p>
            <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:'8px',padding:'10px',fontSize:'12px',marginBottom:'12px'}}>
              {remarkModal.changes.map((c,i) => (
                <div key={i} style={{padding:'2px 0'}}><b>{c.field}</b>: <span style={{color:'#b91c1c'}}>{c.old_value || '—'}</span> → <span style={{color:'#15803d'}}>{c.new_value || '—'}</span></div>
              ))}
            </div>
            <label style={{fontSize:'12px',fontWeight:600,color:'var(--text-muted)'}}>Remark <span style={{color:'var(--accent)'}}>*</span></label>
            <textarea autoFocus className="form-control" rows={3} value={remarkModal.note} onChange={e=>setRemarkModal({...remarkModal, note: e.target.value})} placeholder="e.g. Shipper changed shipping line; weight revised after re-weighment" />
            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',marginTop:'14px'}}>
              <button className="btn btn-secondary btn-sm" onClick={()=>setRemarkModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={()=>{ if (!remarkModal.note.trim()) return toast.error('Remark is required'); doSave(remarkModal.note.trim()); }} disabled={saving || !remarkModal.note.trim()}>{saving?'Saving…':'Save with Remark'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
